/**
 * useInvestorData — fetches GET /api/v1/investor and returns the 12-KPI block.
 *
 * Behaviour:
 *   • Calls the backend with a 5-second timeout.
 *   • Accepts an optional `range` ({from, to}) so the date pill can drive
 *     the scale + virality tiles. Wallet balances stay as a current snapshot
 *     regardless (the platform's liability has no time dimension).
 *   • Per CEO directive 2026-04-28: NO mock fallback. On any failure, `data`
 *     stays null and `error` carries the failure reason — the UI is
 *     responsible for rendering an explicit error state so an investor never
 *     sees a fabricated number.
 *   • Re-fetches on `refreshKey` change OR when the range changes.
 *
 * The shape contract here MUST match `routes/investor.routes.js` exactly —
 * if the contract changes, the integration test on the backend side catches
 * it, and this file gets updated in lockstep.
 */
import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 5000;

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`timeout after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useInvestorData(range = null, rangeId = null, refreshKey = 0) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Stable serialisation of the range so the effect doesn't loop on identity.
  const rangeKey = range ? `${range.from || ''}|${range.to || ''}|${rangeId || ''}` : '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    try {
      const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = {};
      if (token)       headers.Authorization = `Bearer ${token}`;
      else if (apiKey) headers["X-API-Key"]  = apiKey;
      const params = new URLSearchParams();
      if (range && range.from) params.set('from', range.from);
      if (range && range.to)   params.set('to',   range.to);
      // Pill identity drives apples-to-apples comparisons in the backend.
      if (rangeId) params.set('rangeId', rangeId);
      const qs = params.toString();
      const url = `${API_BASE}/investor${qs ? `?${qs}` : ''}`;
      const res = await withTimeout(
        fetch(url, {
          headers,
          signal: controller.signal,
        }),
        FETCH_TIMEOUT_MS,
        controller,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
      setData(json.data);
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, rangeKey]);

  return { data, error, loading, reload: load };
}
